/** What a v2 envelope card shows (TMMAPP-0151 §F 18): the kind chip, the served figures and the target line. */
import type { TFunction } from 'i18next';
import type { BalancePoint, Envelope } from '@/api/envelopes';
import { ENVELOPE_KIND_TINT } from '@/components/envelopeKindTint';
import { bpToPercentText, formatMoneyMinor } from '@/i18n/format';
import type { TintName } from '@/theme/categorical';

export type EnvelopeProgressState = 'progress' | 'over' | 'reached';

/** The bar's state from the served progress: a budget past its target, a goal that reached it. */
export function envelopeProgressState(
  kind: Envelope['kind'],
  progressBp: number | null,
): EnvelopeProgressState {
  if (progressBp === null) return 'progress';
  if (kind === 'BUDGET' && progressBp > 10000) return 'over';
  if (kind === 'SAVINGS' && progressBp >= 10000) return 'reached';
  return 'progress';
}

export interface EnvelopeTileContent {
  name: string;
  /** The kind chip's tint and label; null for the unassigned pool. */
  kind: { tint: TintName; label: string } | null;
  balanceMinor: number | null;
  /** The month-end bars; null draws none. */
  series: readonly BalancePoint[] | null;
  /** The served progress toward the target, in basis points; null (none served) draws no bar. */
  progressBp: number | null;
  /**
   * The bar's state (TMMAPP-0154 item 13): `over` a budget past 100 %, `reached` a goal at 100 % or
   * more, else `progress`.
   */
  progressState: EnvelopeProgressState;
  line: string;
}

/** An envelope's card content: the kind chip, the served progress and its target line. */
export function envelopeTileContent(
  envelope: Envelope,
  currency: string | null,
  locale: string,
  t: TFunction,
): EnvelopeTileContent {
  const target = envelope.kind !== 'CASH' ? envelope.targetMinor : null;
  const tint = ENVELOPE_KIND_TINT[envelope.kind];
  // No invented progress (TMMAPP-0154 item 13): with none served, no percent and no bar.
  const progressBp = target === null ? null : envelope.progressBp;
  const budget = envelope.kind === 'BUDGET';
  // A target, budget or goal is in whole units (contract v2 §13).
  const targetText =
    target === null ? '' : formatMoneyMinor(target, currency, locale, false, { whole: true });
  return {
    name: envelope.name,
    kind: { tint, label: t(`envelope.kindChip.${envelope.kind}`) },
    balanceMinor: envelope.balanceMinor,
    series: envelope.balanceSeries,
    progressBp,
    progressState: envelopeProgressState(envelope.kind, progressBp),
    line:
      target === null
        ? t('virtualAccounts.panel.noTarget')
        : progressBp === null
          ? t(budget ? 'virtualAccounts.panel.targetBudget' : 'virtualAccounts.panel.targetGoal', {
              target: targetText,
            })
          : t(budget ? 'virtualAccounts.panel.ofBudget' : 'virtualAccounts.panel.ofGoal', {
              percent: bpToPercentText(progressBp, locale),
              target: targetText,
            }),
  };
}

/** The unassigned pool's card content (the money in no envelope, served). */
export function unassignedTileContent(
  balanceMinor: number | null,
  t: TFunction,
): EnvelopeTileContent {
  return {
    name: t('virtualAccount.unassigned'),
    kind: null,
    balanceMinor,
    series: null,
    progressBp: null,
    progressState: 'progress',
    line: t('virtualAccounts.panel.unassignedLine'),
  };
}
